import type { Traduttore } from "../../i18n/index.ts";
import type { Issue, VariableStatus } from "./types.ts";

/**
 * Riscontro immediato sui problemi che si stanno correggendo.
 *
 * La validazione è del server e guarda l'ultima revisione salvata: da sola
 * direbbe «manca DATABASE_URL» anche mentre la si sta scrivendo. Queste funzioni
 * anticipano l'esito sulle modifiche ancora in corso, così l'interfaccia mostra
 * il problema chiudersi nel momento in cui viene risolto, invece di attendere un
 * salvataggio per confermare qualcosa che è già evidente.
 *
 * Restano un'anticipazione, non un verdetto: la verità è quella del server, e
 * l'interfaccia lo dichiara chiedendo di salvare e ricontrollare.
 */

/** Un valore conta come impostato solo se contiene qualcosa di sostanziale. */
export const haValore = (valore: string | undefined): boolean =>
  valore !== undefined && valore.trim() !== "";

/**
 * Se un problema risulta chiuso dall'environment in corso di modifica.
 *
 * Solo i problemi delle variabili si risolvono da qui: tag, immagini e porte
 * vivono nel compose, e segnarli risolti perché si è scritta una variabile
 * sarebbe falso.
 */
export function problemaRisolto(problema: Issue, env: Record<string, string>): boolean {
  if (!problema.variable) return false;

  if (problema.code === "MISSING_VARIABLE") return haValore(env[problema.variable]);

  // Una variabile inutilizzata si risolve togliendola, non riempiendola.
  if (problema.code === "UNUSED_VARIABLE") return !(problema.variable in env);

  return false;
}

/** Quanti problemi dell'elenco risultano già chiusi dalle modifiche in corso. */
export function contaRisolti(problemi: readonly Issue[], env: Record<string, string>): number {
  return problemi.filter((problema) => problemaRisolto(problema, env)).length;
}

/**
 * Testo del campo vuoto di una variabile.
 *
 * Una variabile con valore predefinito nel compose ha un campo vuoto ma un
 * valore reale: tacerlo la farebbe sembrare da compilare, e scriverci qualcosa
 * per sicurezza significa sostituire un default che andava bene.
 */
export function segnaposto(
  variabile: { status: VariableStatus; effectiveValue: string | null },
  t: Traduttore,
): string {
  if (variabile.status === "default" && haValore(variabile.effectiveValue ?? undefined)) {
    return t.t("verifica.segnapostoPredefinito", { valore: variabile.effectiveValue! });
  }

  return t.t("verifica.segnaposto");
}

/**
 * Nomi delle variabili che differiscono dall'ultimo salvataggio.
 *
 * Ordinati per nome: l'ordine delle chiavi di un `Record` non è garantito, e
 * confrontarlo farebbe apparire modificato uno stack intatto.
 */
export function variabiliModificate(
  originale: Record<string, string>,
  corrente: Record<string, string>,
): string[] {
  const nomi = new Set([...Object.keys(originale), ...Object.keys(corrente)]);

  return [...nomi].filter((nome) => originale[nome] !== corrente[nome]).sort();
}
